/*
 * Email-code step-up for sensitive payment edits (backend P1, docs/backend-p1-rpc-contract.md §1).
 *
 *   1. functions.invoke('admin-step-up', { action: 'request', purpose }) emails a 6-digit code
 *      to the caller's own sign-in email (never in the response).
 *   2. rpc('admin_verify_step_up', { p_code, p_purpose }) → { verified, step_up_token, expires_at }
 *      or { verified: false, reason, attempts_left } (answered, not raised).
 *   3. The token goes to the protected RPC as p_step_up_token. It is single use, 5 minutes,
 *      bound to the caller and the purpose, and a failed write does not burn it.
 *
 * Tokens live in memory only (never storage), one per purpose, until used or expired.
 */
import { supabase } from "@/integrations/supabase/client";
import { db } from "@/lib/db";

export type StepUpPurpose = "payment_account" | "payment_rail";

export const STEP_UP_PURPOSE_TEXT: Record<StepUpPurpose, string> = {
  payment_account: "change a pay-to account",
  payment_rail: "turn on a payment method",
};

/** The person closed the code dialog. Callers treat it as "nothing happened". */
export class StepUpCancelledError extends Error {
  constructor() {
    super("Cancelled");
    this.name = "StepUpCancelledError";
  }
}

export const isStepUpCancelled = (e: unknown) => e instanceof StepUpCancelledError;

/** The RPC refused for want of a (valid) token: step_up_required / step_up_invalid. */
export const isStepUpError = (e: unknown) => {
  const msg = String((e as { message?: string; detail?: string; code?: string } | null)?.message ?? "");
  const code = String((e as { code?: string } | null)?.code ?? "");
  return code === "step_up_required" || code === "step_up_invalid" || /^step_up_(required|invalid)\b/.test(msg.trim());
};

// ───────────────────────── Token cache (memory only) ─────────────────────────

const cache = new Map<StepUpPurpose, { token: string; expiresAt: number }>();
/** Keep a margin so a token never expires between the check and the RPC. */
const MARGIN_MS = 20_000;

/** Removes and returns a live token for this purpose (single use). */
export const takeStepUpToken = (purpose: StepUpPurpose): { token: string; expiresAt: number } | null => {
  const hit = cache.get(purpose);
  cache.delete(purpose);
  if (!hit || hit.expiresAt - MARGIN_MS <= Date.now()) return null;
  return hit;
};

/** Puts a token back after a write failed for another reason (the server didn't burn it). */
export const keepStepUpToken = (purpose: StepUpPurpose, token: string, expiresAt: number) => {
  if (expiresAt - MARGIN_MS > Date.now()) cache.set(purpose, { token, expiresAt });
};

export const clearStepUpTokens = () => cache.clear();

// ───────────────────────── Edge-function helper ─────────────────────────

export interface FnResult<T> {
  ok: boolean;
  status: number;
  data: T;
}

type J = Record<string, unknown>;

/** Invokes an edge function and always returns the JSON body, even on a 4xx/5xx. */
export const invokeFunction = async <T = J,>(name: string, body: J): Promise<FnResult<T>> => {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (!error) return { ok: true, status: 200, data: (data ?? {}) as T };
  const ctx = (error as { context?: Response }).context;
  if (ctx && typeof ctx.status === "number") {
    let parsed: unknown = {};
    try {
      parsed = await ctx.clone().json();
    } catch {
      try {
        parsed = { error: await ctx.text() };
      } catch {
        parsed = {};
      }
    }
    return { ok: false, status: ctx.status, data: (parsed ?? {}) as T };
  }
  // Network / relay failure: no response at all.
  throw new Error(/fetch|network/i.test(error.message) ? "Can't reach the server. Check the connection and retry." : error.message);
};

// ───────────────────────── Steps 1 and 2 ─────────────────────────

export interface StepUpSent {
  emailMasked: string;
  expiresAt: string | null;
}

const SEND_ERRORS: Record<string, string> = {
  rate_limited: "Too many codes asked for. Wait a few minutes (5 codes per 15 minutes), then send a new one.",
  forbidden: "Only a Super Admin can confirm this change.",
  invalid_purpose: "This change can't be confirmed with a code. Reload the page and try again.",
  email_failed: "The code email couldn't be sent. Try again in a minute.",
  Unauthorized: "You are signed out. Sign in again and retry.",
};

export const requestStepUpCode = async (purpose: StepUpPurpose): Promise<StepUpSent> => {
  const r = await invokeFunction<{ sent?: boolean; email_masked?: string; expires_at?: string; error?: string; message?: string }>(
    "admin-step-up",
    { action: "request", purpose }
  );
  if (r.ok && r.data.sent) return { emailMasked: r.data.email_masked ?? "your email", expiresAt: r.data.expires_at ?? null };
  const code = r.data.error ?? (r.status === 403 ? "forbidden" : r.status === 429 ? "rate_limited" : "");
  throw new Error(SEND_ERRORS[code] ?? (r.data.message || `Couldn't send the code (${r.status}).`));
};

export interface StepUpVerify {
  verified: boolean;
  /** Set when verified. */
  token?: string;
  expiresAt?: number;
  /** Set when not verified. */
  reason?: "wrong_code" | "expired" | "too_many_attempts";
  attemptsLeft?: number;
}

export const verifyStepUpCode = async (purpose: StepUpPurpose, code: string): Promise<StepUpVerify> => {
  const { data, error } = await db.rpc("admin_verify_step_up", { p_code: code.trim(), p_purpose: purpose });
  if (error) {
    if (error.code === "42501") throw new Error(SEND_ERRORS.forbidden);
    throw new Error(error.message || "Couldn't check the code.");
  }
  const d = (data ?? {}) as J;
  if (d.verified === true && typeof d.step_up_token === "string") {
    const expiresAt = d.expires_at ? new Date(String(d.expires_at)).getTime() : Date.now() + 5 * 60_000;
    return { verified: true, token: d.step_up_token, expiresAt };
  }
  const reason = (["wrong_code", "expired", "too_many_attempts"] as const).find((x) => x === d.reason) ?? "wrong_code";
  return { verified: false, reason, attemptsLeft: Number(d.attempts_left ?? 0) };
};
